// ─── Module inventory — détail d'un inventaire (VUE ARBRE) ────────
// Fidèle à MoPCal : arbre catégories → éléments → articles en accordéons,
// pastille de comptage, décompte en « pièces », et surtout CLIC = FICHE EN
// LECTURE SEULE (règle app-wide ; ✏️/🗑️ sur la fiche). Édition par la feuille.
//
// En-tête (mode édition) : 📋 comptage · ➕ ajouter · ⚙️ catégories · 📷 scan · 📤
// En `readonly` (MoPCal), tout est consultable, rien n'est modifiable.
import React, { useEffect, useMemo, useState } from 'react';
import {
  View, Text, Image, TouchableOpacity, ScrollView, Modal, TextInput, StyleSheet,
  ActivityIndicator,
} from 'react-native';
import {
  useUI, useAuth, useEspace, PageHeader, EmptyState, LigneSwipe, SelecteurEmoji, confirmerSuppression,
  useMenuOngletActif, GesteRetourEcran, Feuille, useT,
} from '@mop/core';
import {
  ecouterItems, lireInventaire, ajouterItem, modifierItem, supprimerItem, marquerCompte,
  modifierArbre, modifierProprietes, enregistrerModeleInventaire, ecouterEmplacements, importerInventaire, chercherParCodeBarres,
  aplatirArbre, cheminDe, noeudParId, parentDe, idsDescendants, nouvelId,
  qteItem, seuilItem, sousSeuil, articlesDe, photosArticle, dernierInventaireDe,
  roleInventaire,
} from './services/inventaires';
import { messageErreur } from './services/erreurs';
import FeuilleElement, { etatDepuisItem, itemDepuisEtat } from './FeuilleElement';
import FeuilleTransfert from './FeuilleTransfert';
import FeuilleProprietes from './FeuilleProprietes';
import FicheElement from './FicheElement';
import LocalisationModal from './LocalisationModal';
import ScannerCodeBarres from './ui/ScannerCodeBarres';

const clone = (x) => JSON.parse(JSON.stringify(x || []));
const HIT = { top: 8, bottom: 8, left: 8, right: 8 };
const dateCourte = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }); }
  catch { return null; }
};

export default function EcranInventaire({ inventaireId, mode = 'edit', onRetour, ongletCle = 'Inventaires' }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { tenantId, tenantRangement, espaceActif } = useEspace();
  const appEdit = mode === 'edit';

  const [inv, setInv]          = useState(null);
  const [items, setItems]      = useState([]);
  const [emplacements, setEmp] = useState([]);
  const [charge, setCharge]    = useState(false);
  const [err, setErr]          = useState('');
  const [info, setInfo]        = useState('');

  const [recherche, setRech]   = useState('');
  const [masquerVides, setMV]  = useState(true);
  const [comptage, setComptage] = useState(false);
  const [catPlies, setCatPlies] = useState(() => new Set());  // catégories repliées (défaut : ouvertes)
  const [elemOuv, setElemOuv]   = useState(() => new Set());  // éléments dépliés (défaut : repliés)

  // Feuilles
  const [fiche, setFiche]   = useState(null);   // { item, articleIndex } — lecture seule
  const [elem, setElem]     = useState(null);   // état d'édition d'un élément
  const [elemBase, setBase] = useState('');
  const [cfg, setCfg]       = useState(false);  // ⚙️ mode configuration DE L'ARBRE (comme MoPCal)
  const [configHub, setHub] = useState(false);  // écran « Configuration » (liste des possibilités)
  const [propsSheet, setPropsSheet] = useState(false);  // feuille propriétés personnalisées
  const [modeleSheet, setModeleSheet] = useState(null); // { nom, emoji } — enregistrer la structure comme modèle
  const [fCat, setFCat]     = useState(null);
  const [pickParent, setPick] = useState(false);
  const [transfert, setTransfert] = useState(false);
  const [scan, setScan]     = useState(false);
  const [locId, setLocId]   = useState(null);   // emplacement à localiser (« Où est-ce rangé ? »)
  const [busy, setBusy]     = useState(false);

  useEffect(() => { lireInventaire(inventaireId).then(setInv).catch(e => setErr(messageErreur(e))); }, [inventaireId]);
  useEffect(() => {
    const unsub = ecouterItems(inventaireId, (l) => { setItems(l); setCharge(true); });
    return () => unsub();
  }, [inventaireId]);
  useEffect(() => {
    if (!tenantRangement) { setEmp([]); return undefined; }
    return ecouterEmplacements(tenantRangement, (arbre) => setEmp(arbre));
  }, [tenantRangement]);

  const arbre   = inv?.arbre || [];
  const props   = inv?.proprietes || [];
  const cats    = useMemo(() => aplatirArbre(arbre), [arbre]);
  const dossier = `espaces/${tenantId}/inventaire/${inventaireId}`;

  // Droits sur CET inventaire (un espace = un responsable) :
  //   • peutStructure : responsable → arbre, propriétés, partage, modèle
  //   • edit (= peut données) : responsable OU éditeur → articles/quantités
  //   • sinon lecture seule.
  const role = roleInventaire(inv, user?.uid, espaceActif?.creePar);
  const peutStructure = appEdit && role === 'proprietaire';
  const edit = appEdit && (role === 'proprietaire' || role === 'redacteur');

  // Appui long sur l'onglet → « Configuration » (structure) : responsable seul.
  useMenuOngletActif(ongletCle, [
    { label: t('calendrier.configuration'), icone: '⚙️', onPress: () => setHub(true) },
  ], peutStructure);

  async function enregistrerProps(liste) {
    try { await modifierProprietes(inventaireId, liste); setInv(v => ({ ...v, proprietes: liste })); }
    catch (e) { setErr(messageErreur(e)); }
  }

  const modeleModifie = !!modeleSheet && !!modeleSheet.nom.trim();
  async function enregistrerModele() {
    if (!modeleModifie || busy) return;
    setBusy(true); setErr('');
    try {
      await enregistrerModeleInventaire({
        tenantId, creePar: user?.uid, creeParNom: user?.prenom || user?.email || '',
        nom: modeleSheet.nom.trim(), emoji: modeleSheet.emoji || '📦',
        desc: t('inventaire.modeleMeta', { c: aplatirArbre(arbre).length, p: props.length }),
        arbre, proprietes: props,
      });
      setModeleSheet(null); setHub(false);
      setInfo(t('inventaire.modeleEnregistre', { nom: modeleSheet.nom.trim() }));
    } catch (e) { setErr(messageErreur(e)); }
    setBusy(false);
  }

  // Décompte MoPCal : « pièces » = somme des quantités.
  const nbPieces = useMemo(() => items.reduce((n, it) => n + qteItem(it), 0), [items]);
  const enAlerte = useMemo(() => items.filter(sousSeuil).length, [items]);
  const dernier  = useMemo(() => {
    const d = dernierInventaireDe(items);
    if (!d) return null;
    try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }); }
    catch { return null; }
  }, [items]);

  // Filtre de recherche (nom élément / article / code-barres).
  const q = recherche.trim().toLowerCase();
  const matchItem = (it) => {
    if (!q) return true;
    if ((it.nom || '').toLowerCase().includes(q)) return true;
    return articlesDe(it).some(a =>
      (a.nom || '').toLowerCase().includes(q) || String(a.codeBarres || '').includes(q));
  };
  const itemsVisibles = useMemo(() => items.filter(matchItem), [items, q]);

  // Éléments par catégorie (catId → items). `__racine__` = sans catégorie.
  const parCat = useMemo(() => {
    const m = new Map();
    itemsVisibles.forEach(it => {
      const k = it.catId || '__racine__';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(it);
    });
    for (const arr of m.values()) arr.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
    return m;
  }, [itemsVisibles]);

  const toggleCat  = (id) => setCatPlies(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleElem = (id) => setElemOuv(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const piecesSousCat = (catId) => {
    const ids = new Set(idsDescendants(arbre, catId));
    return items.filter(it => ids.has(it.catId)).reduce((n, it) => n + qteItem(it), 0);
  };

  // ── Actions élément ──────────────────────────────────────────────
  // `catId` : catégorie pré-sélectionnée (bouton « + élément ici »).
  function ouvrirElem(it, prefill, catId = null) {
    const e = etatDepuisItem(it, catId ?? null);
    if (prefill) {
      e.nom = prefill.nom || '';
      e.articles[0] = { ...e.articles[0], nom: prefill.nom || '', codeBarres: prefill.code || null,
                        photos: prefill.photoUrl ? [{ url: prefill.photoUrl }] : [] };
    }
    setFiche(null); setElem(e); setBase(JSON.stringify(e));
  }
  const elemModifie = !!elem && !!elem.nom.trim() && JSON.stringify(elem) !== elemBase;

  async function enregistrerElem() {
    if (!elem || !elemModifie || busy) return;
    setBusy(true); setErr('');
    try {
      const champs = itemDepuisEtat(elem);
      if (elem.id) await modifierItem(inventaireId, elem.id, champs);
      else         await ajouterItem(inventaireId, { ...champs, creePar: user?.uid });
      setElem(null);
    } catch (e) { setErr(messageErreur(e)); }
    setBusy(false);
  }

  function supprimerItemConfirm(it) {
    confirmerSuppression(t('inventaire.supprElementTitre'), t('inventaire.elementSeraSupprime', { nom: it.nom }),
      async () => { try { await supprimerItem(inventaireId, it.id); setFiche(null); } catch (e) { setErr(messageErreur(e)); } });
  }
  const ouvrirFiche = (it, articleIndex) => setFiche({ item: it, articleIndex });

  // Supprime UN article d'un élément (ou l'élément entier si c'était le dernier).
  function supprimerArticle(it, articleId) {
    const arts = articlesDe(it);
    const cible = arts.find(x => x.id === articleId);
    confirmerSuppression(t('inventaire.supprArticleTitre'), t('inventaire.elementSeraSupprime', { nom: cible?.nom || it.nom }), async () => {
      try {
        const restant = arts.filter(x => x.id !== articleId);
        if (restant.length === 0) { await supprimerItem(inventaireId, it.id); setFiche(null); }
        else await modifierItem(inventaireId, it.id, { articles: restant });
      } catch (e) { setErr(messageErreur(e)); }
    });
  }

  // ── Scan ─────────────────────────────────────────────────────────
  function apresScan(code) {
    setScan(false);
    const trouve = chercherParCodeBarres(items, code);
    if (trouve) { setInfo(t('inventaire.trouve', { nom: trouve.item.nom })); ouvrirFiche(trouve.item, trouve.index); return; }
    setInfo(t('inventaire.codeInconnu', { code }));
    ouvrirElem(null, { code });
  }

  const compter = (it) => marquerCompte(inventaireId, it.id).catch(e => setErr(messageErreur(e)));

  // ── Catégories (arbre) ───────────────────────────────────────────
  // `id` : catégorie à éditer. `parentPourNouveau` : parent d'une nouvelle
  // sous-catégorie (bouton ➕ sur une ligne de catégorie).
  const ouvrirFCat = (id = null, parentPourNouveau = null) => {
    const n = id ? noeudParId(arbre, id) : null;
    let parentActuel = parentPourNouveau;
    if (id) { const p = parentDe(arbre, id); parentActuel = p && p.parent ? p.parent.id : null; }
    setFCat({
      id: id || null, parentActuel, nouveauParentId: parentActuel,
      nom: n ? n.nom : '', emoji: n ? (n.emoji || '📁') : '📁',
      nomInitial: n ? n.nom : '', emojiInitial: n ? (n.emoji || '📁') : '📁',
    });
    setPick(false);
  };
  const catModifie = !!fCat && !!fCat.nom.trim() && (
    !fCat.id || fCat.nom.trim() !== fCat.nomInitial || fCat.emoji !== fCat.emojiInitial
    || (fCat.nouveauParentId || null) !== (fCat.parentActuel || null)
  );
  async function enregistrerCat() {
    if (!fCat || !catModifie || busy) return;
    setBusy(true); setErr('');
    try {
      const t2 = clone(arbre);
      const nom = fCat.nom.trim();
      const nvParent = fCat.nouveauParentId || null;
      if (fCat.id) {
        const n = noeudParId(t2, fCat.id);
        if (n) {
          n.nom = nom; n.emoji = fCat.emoji || '📁';
          if (nvParent !== (fCat.parentActuel || null)) {
            const desc = new Set(idsDescendants(t2, n.id));
            if (!nvParent || !desc.has(nvParent)) {
              const pc = parentDe(t2, n.id);
              if (pc) { const i = pc.liste.findIndex(x => x.id === n.id); if (i >= 0) pc.liste.splice(i, 1); }
              if (nvParent) { const np = noeudParId(t2, nvParent); if (np) { np.enfants = np.enfants || []; np.enfants.push(n); } else t2.push(n); }
              else t2.push(n);
            }
          }
        }
      } else {
        const node = { id: nouvelId('c'), nom, emoji: fCat.emoji || '📁', enfants: [] };
        if (nvParent) { const p = noeudParId(t2, nvParent); if (p) { p.enfants = p.enfants || []; p.enfants.push(node); } else t2.push(node); }
        else t2.push(node);
      }
      await modifierArbre(inventaireId, t2);
      setInv(v => ({ ...v, arbre: t2 }));
      setFCat(null);
    } catch (e) { setErr(messageErreur(e)); }
    setBusy(false);
  }
  function supprimerCat(n) {
    const ids = new Set(idsDescendants(arbre, n.id));
    const touches = items.filter(it => ids.has(it.catId)).length;
    confirmerSuppression(
      t('inventaire.supprCatTitre'),
      t('inventaire.supprCatMsgBase', { nom: n.nom }) +
      (touches ? t('inventaire.supprCatMsgElements', { n: touches }) : ''),
      async () => {
        try {
          for (const it of items) if (ids.has(it.catId)) { try { await modifierItem(inventaireId, it.id, { catId: null }); } catch (_) {} }
          const t2 = clone(arbre);
          const p = parentDe(t2, n.id);
          if (p) { const i = p.liste.findIndex(x => x.id === n.id); if (i >= 0) p.liste.splice(i, 1); }
          else { const i = t2.findIndex(x => x.id === n.id); if (i >= 0) t2.splice(i, 1); }
          await modifierArbre(inventaireId, t2);
          setInv(v => ({ ...v, arbre: t2 }));
        } catch (e) { setErr(messageErreur(e)); }
      });
  }
  const exclusCat = fCat?.id ? new Set(idsDescendants(arbre, fCat.id)) : new Set();

  async function importer(prep) {
    const r = await importerInventaire(tenantId, prep, {
      uid: user?.uid, nom: user?.prenom || user?.email, tenantHerite: tenantRangement, tenantRangement,
    });
    setInfo(t('inventaire.importe', { n: r.nbItems }));
    setTransfert(false);
  }

  // ── Rendu d'un ÉLÉMENT (accordéon vers ses articles) ─────────────
  // Un élément est TOUJOURS un conteneur : mono ou multi, il se déplie sur
  // ses article(s). Le tap sur l'élément = déplier ; le tap sur un article =
  // fiche. (Convention MoPCal : mono affiché comme multi.)
  function rendreElement(it, prof) {
    const arts = articlesDe(it);
    const ouvert = elemOuv.has(it.id) || !!q;   // recherche → tout déplié
    const compte = dateCourte(it.compteLe);
    const alerte = sousSeuil(it);
    const photo0 = photosArticle(arts[0])[0];
    return (
      <View key={it.id}>
        {/* Élément géré comme une ligne de liste : ← supprimer, → éditer. */}
        <LigneSwipe espacement={6} enabled={edit}
          onSupprimer={edit ? () => supprimerItemConfirm(it) : undefined}
          onEditer={edit ? () => ouvrirElem(it) : undefined}>
          <TouchableOpacity
            style={[s.elem, { marginLeft: prof * 14 }, alerte && s.elemAlerte]}
            activeOpacity={0.7}
            onPress={() => (comptage && edit ? compter(it) : toggleElem(it.id))}
            onLongPress={edit ? () => ouvrirElem(it) : undefined} delayLongPress={450}>
            <View style={[s.pastilleCompte, compte && s.pastilleOk]} />
            {photo0
              ? <Image source={{ uri: photo0.url }} style={s.elemPhoto} />
              : <Text style={s.elemEmoji}>{it.emoji || '📦'}</Text>}
            <View style={{ flex: 1 }}>
              <Text style={s.elemNom} numberOfLines={1}>{it.nom}</Text>
              <Text style={s.elemSous} numberOfLines={1}>
                {t('inventaire.nArticles', { n: arts.length })}
                {seuilItem(it) > 0 ? ` · ${t('inventaire.seuil')} ${seuilItem(it)}` : ''}
              </Text>
            </View>
            {comptage && edit ? (
              <View style={s.compter}><Text style={s.compterTxt}>{compte ? '✓' : t('inventaire.compter')}</Text></View>
            ) : (
              <>
                <Text style={[s.badge, alerte && { color: UI.danger }]}>{qteItem(it)}</Text>
                <Text style={s.chevMini}>{ouvert ? '▾' : '›'}</Text>
              </>
            )}
          </TouchableOpacity>
        </LigneSwipe>

        {ouvert && arts.map((a, i) => {
          const ph = photosArticle(a)[0];
          const etat = a.dernierPointage?.etat;
          return (
            // Article géré aussi en ligne de liste : ← supprimer cet article, → éditer.
            <LigneSwipe key={a.id} espacement={5} enabled={edit}
              onSupprimer={edit ? () => supprimerArticle(it, a.id) : undefined}
              onEditer={edit ? () => ouvrirElem(it) : undefined}>
              <TouchableOpacity style={[s.art, { marginLeft: prof * 14 + 24 }]}
                activeOpacity={0.7} onPress={() => ouvrirFiche(it, i)}>
                <View style={[s.pastilleCompte, etat === 'present' && s.pastilleOk, etat === 'absent' && s.pastilleKo]} />
                {ph ? <Image source={{ uri: ph.url }} style={s.artPhoto} /> : <Text style={s.artEmoji}>🏷️</Text>}
                <View style={{ flex: 1 }}>
                  <Text style={s.artNom} numberOfLines={1}>{a.nom || it.nom}</Text>
                  {!!a.emplacementId && (
                    <TouchableOpacity onPress={() => setLocId(a.emplacementId)} hitSlop={{ top: 6, bottom: 6, left: 0, right: 6 }}>
                      {/* Dernier niveau seulement (le tap ouvre le chemin complet). */}
                      <Text style={s.artLieu} numberOfLines={1}>📍 {noeudParId(emplacements, a.emplacementId)?.nom || '—'}</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <Text style={s.artQte}>{a.unique ? (etat === 'absent' ? '—' : '1') : (Number(a.qte) || 0)}</Text>
              </TouchableOpacity>
            </LigneSwipe>
          );
        })}
      </View>
    );
  }

  // ── Rendu récursif d'une CATÉGORIE ───────────────────────────────
  // En mode `cfg` (⚙️), l'arbre lui-même est éditable comme dans MoPCal :
  // swipe sur la catégorie (← supprimer, → éditer), ➕ pour une sous-catégorie
  // sur la ligne, et « ＋ Ajouter un élément ici » sous la catégorie dépliée.
  function rendreCat(node, prof) {
    const elems = parCat.get(node.id) || [];
    const sousCats = node.enfants || [];
    const pieces = piecesSousCat(node.id);
    if (masquerVides && pieces === 0 && !q && !cfg) return null;
    const plie = catPlies.has(node.id);
    const ligne = (
      <TouchableOpacity style={[s.cat, { marginLeft: prof * 14 }]} activeOpacity={0.7} onPress={() => toggleCat(node.id)}>
        <Text style={s.catChev}>{plie ? '▸' : '▾'}</Text>
        <Text style={s.catEmoji}>{node.emoji || '📁'}</Text>
        <Text style={s.catNom} numberOfLines={1}>{node.nom}</Text>
        {cfg
          ? <TouchableOpacity hitSlop={HIT} onPress={() => ouvrirFCat(null, node.id)} accessibilityLabel={t('inventaire.sousCategorieAcc')}><Text style={s.catPlus}>＋</Text></TouchableOpacity>
          : <Text style={s.catBadge}>{pieces}</Text>}
      </TouchableOpacity>
    );
    return (
      <View key={node.id}>
        {cfg
          ? <LigneSwipe espacement={6} onSupprimer={() => supprimerCat(node)} onEditer={() => ouvrirFCat(node.id)}>{ligne}</LigneSwipe>
          : ligne}
        {!plie && (
          <>
            {sousCats.map(sc => rendreCat(sc, prof + 1))}
            {/* En config de STRUCTURE, on ne montre PAS les données (éléments) :
                la structure et les données sont dissociées (les éléments se
                gèrent dans la vue normale, avec ＋ et le swipe). */}
            {!cfg && elems.map(it => rendreElement(it, prof + 1))}
          </>
        )}
      </View>
    );
  }

  const racineElems = parCat.get('__racine__') || [];

  // Écran « Configuration » : liste des possibilités (structure, propriétés…).
  const OPTIONS_CONFIG = [
    { emoji: '🌳', titre: t('inventaire.structureInventaire'), sous: t('inventaire.structureInventaireSous'), on: () => { setHub(false); setCfg(true); } },
    { emoji: '🏷️', titre: t('inventaire.proprietesPersonnalisees'), sous: t('inventaire.proprietesPersoSous'), on: () => setPropsSheet(true) },
    { emoji: '📑', titre: t('inventaire.enregistrerModele'), sous: t('inventaire.enregistrerModeleSous'), on: () => setModeleSheet({ nom: inv?.nom || '', emoji: inv?.emoji || '📦' }) },
    { emoji: '📤', titre: t('inventaire.exporterTransferer'), sous: t('inventaire.exporterTransfererSous'), on: () => setTransfert(true) },
  ];

  // Retour courant (pour le bouton ‹ ET le geste de balayage bord gauche) :
  // hub → écran, config arbre → normal, sinon détail → liste.
  const retourCourant = configHub ? () => setHub(false) : (cfg ? () => setCfg(false) : onRetour);
  const modaleOuverte = !!fiche || !!elem || transfert || propsSheet || !!modeleSheet || scan || !!fCat || !!locId;

  return (
    <GesteRetourEcran actif={!!retourCourant && !modaleOuverte} onRetour={retourCourant}>
    <View style={s.page}>
      {configHub ? (
        <>
          <PageHeader titre={t('calendrier.configuration')} iconeTitre="⚙️" onRetour={() => setHub(false)} />
          {!!err && <TouchableOpacity style={s.err} onPress={() => setErr('')}><Text style={s.errTxt}>⚠️ {err}</Text></TouchableOpacity>}
          <ScrollView contentContainerStyle={s.corps}>
            <Text style={s.cfgIntro}>{t('inventaire.reglagesDe', { nom: inv?.nom || t('inventaire.cetInventaire') })}</Text>
            {OPTIONS_CONFIG.map((o, i) => (
              <TouchableOpacity key={i} style={s.cfgCard} activeOpacity={0.7} onPress={o.on}>
                <Text style={s.cfgEmoji}>{o.emoji}</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.cfgTitre}>{o.titre}</Text>
                  <Text style={s.cfgSous}>{o.sous}</Text>
                </View>
                <Text style={s.cfgChev}>›</Text>
              </TouchableOpacity>
            ))}
            <View style={{ height: 24 }} />
          </ScrollView>
        </>
      ) : (
        <>
      <PageHeader
        titre={inv?.nom || t('inventaire.titreDefaut')} iconeTitre={inv?.emoji || '📦'}
        onRetour={cfg ? () => setCfg(false) : onRetour}>
        {cfg ? (
          <>
            <TouchableOpacity onPress={() => ouvrirFCat(null)} hitSlop={HIT} accessibilityLabel={t('inventaire.categorieRacine')}><Text style={s.hIc}>＋</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => setCfg(false)} hitSlop={HIT} accessibilityLabel={t('tuto.terminer')}><Text style={s.hIc}>✓</Text></TouchableOpacity>
          </>
        ) : edit ? (
          <>
            <TouchableOpacity onPress={() => setComptage(c => !c)} hitSlop={HIT} accessibilityLabel={t('inventaire.faireInventaire')}><Text style={[s.hIc, comptage && s.hIcOn]}>📋</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => ouvrirElem(null)} hitSlop={HIT} accessibilityLabel={t('commun.ajouter')}><Text style={s.hIc}>＋</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => { setInfo(''); setScan(true); }} hitSlop={HIT} accessibilityLabel={t('inventaire.scannerAcc')}><Text style={s.hIc}>📷</Text></TouchableOpacity>
            <TouchableOpacity onPress={() => setTransfert(true)} hitSlop={HIT} accessibilityLabel={t('inventaire.transfererAcc')}><Text style={s.hIc}>📤</Text></TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity onPress={() => setTransfert(true)} hitSlop={HIT} accessibilityLabel={t('inventaire.imprimerAcc')}><Text style={s.hIc}>📤</Text></TouchableOpacity>
        )}
      </PageHeader>

      {!!err && <TouchableOpacity style={s.err} onPress={() => setErr('')}><Text style={s.errTxt}>⚠️ {err}</Text></TouchableOpacity>}
      {!!info && <TouchableOpacity style={s.info} onPress={() => setInfo('')}><Text style={s.infoTxt}>{info}</Text></TouchableOpacity>}

      <View style={s.barre}>
        <Text style={s.barreTxt}>
          {cfg ? t('inventaire.configArbreBarre') : (
            <>
              {t('inventaire.nPiecesN', { n: nbPieces })}
              {enAlerte > 0 ? `  ·  ⚠️ ${enAlerte}` : ''}
              {dernier ? `  ·  ${t('inventaire.dernierInventaire')} ${dernier}` : ''}
            </>
          )}
        </Text>
      </View>
      {!cfg && (
        <>
          <View style={s.rechRow}>
            <Text style={s.rechIc}>🔍</Text>
            <TextInput style={s.rechInput} value={recherche} onChangeText={setRech}
              placeholder={t('inventaire.rechPlaceholder')} placeholderTextColor={UI.faint} autoCorrect={false} />
            <TouchableOpacity style={[s.toggle, masquerVides && s.toggleOn]} onPress={() => setMV(v => !v)}>
              <Text style={s.toggleIc}>🌿</Text>
            </TouchableOpacity>
          </View>
          {masquerVides && <Text style={s.hint}>{t('inventaire.hintVides')}</Text>}
        </>
      )}

      <ScrollView contentContainerStyle={s.corps}>
        {!charge && <ActivityIndicator color={UI.accent} style={{ marginTop: 24 }} />}
        {charge && items.length === 0 && !cfg && (
          <EmptyState icon="🧷" texte={t('inventaire.aucunElementVide')}
            aide={edit ? t('inventaire.aucunElementAide') : t('commun.rienAAfficher')} />
        )}
        {charge && items.length > 0 && itemsVisibles.length === 0 && !cfg && <EmptyState icon="🔍" texte={t('inventaire.aucunResultat')} />}
        {arbre.map(n => rendreCat(n, 0))}
        {cfg && arbre.length === 0 && <EmptyState icon="📁" texte={t('inventaire.aucuneCategorie')} aide={t('inventaire.aucuneCategorieAide')} />}
        {!cfg && racineElems.length > 0 && (
          <>
            {arbre.length > 0 && <Text style={s.sansCat}>{t('inventaire.sansCategorie')}</Text>}
            {racineElems.map(it => rendreElement(it, 0))}
          </>
        )}
        <View style={{ height: 24 }} />
      </ScrollView>
        </>
      )}

      <FicheElement visible={!!fiche} item={fiche?.item} articleIndex={fiche?.articleIndex || 0}
        emplacements={emplacements} arbre={arbre}
        onFermer={() => setFiche(null)}
        onEditer={edit ? () => ouvrirElem(fiche.item) : undefined}
        onSupprimer={edit ? () => supprimerItemConfirm(fiche.item) : undefined} />

      {/* « Où est-ce rangé ? » depuis l'arbre (tap sur le 📍 d'un article). */}
      <LocalisationModal visible={!!locId} emplacementId={locId}
        emplacements={emplacements} onFermer={() => setLocId(null)} />

      <FeuilleElement
        visible={!!elem} etat={elem} setEtat={setElem}
        onEnregistrer={enregistrerElem} onFermer={() => setElem(null)}
        modifie={elemModifie} busy={busy}
        arbre={arbre} emplacements={emplacements} proprietes={props} dossierPhotos={dossier} />

      <FeuilleTransfert visible={transfert} onFermer={() => setTransfert(false)}
        inv={inv} items={items} emplacements={emplacements} onImporter={importer} dossierPhotos={dossier} />

      <FeuilleProprietes visible={propsSheet} proprietes={props}
        onEnregistrer={enregistrerProps} onFermer={() => setPropsSheet(false)} />

      {/* Enregistrer la STRUCTURE comme modèle réutilisable (sans les données). */}
      <Feuille visible={!!modeleSheet} titre={t('inventaire.enregistrerModele')} emoji="📑"
        onFermer={() => setModeleSheet(null)}
        action={{ icone: '＋', onPress: enregistrerModele, disabled: !modeleModifie || busy, label: t('commun.enregistrer') }}>
        <Text style={s.modeleInfo}>
          {t('inventaire.modeleInfoDetail', { c: aplatirArbre(arbre).length, p: props.length })}
        </Text>
        <Text style={s.lbl}>{t('inventaire.nomModele')}</Text>
        <TextInput style={s.input} value={modeleSheet?.nom || ''} autoFocus
          onChangeText={(v) => setModeleSheet(x => ({ ...x, nom: v }))}
          placeholder={t('inventaire.phNomModele')} placeholderTextColor={UI.faint} />
        <Text style={s.lbl}>{t('profil.icone')}</Text>
        <SelecteurEmoji inline avecRecherche valeur={modeleSheet?.emoji || '📦'}
          onChange={(e) => setModeleSheet(x => ({ ...x, emoji: e }))} />
      </Feuille>

      <ScannerCodeBarres visible={scan} mode="barcode" onClose={() => setScan(false)} onScan={apresScan} />

      <Feuille visible={!!fCat} titre={fCat?.id ? t('inventaire.modifierCategorie') : t('inventaire.categorieRacine')} emoji="📁"
        onFermer={() => setFCat(null)}
        action={{ icone: fCat?.id ? '💾' : '＋', onPress: enregistrerCat, disabled: !catModifie || busy, label: t('commun.enregistrer') }}>
              {pickParent ? (
                <>
                  <Text style={s.lbl}>{t('inventaire.rangerDansTitre')}</Text>
                  <TouchableOpacity style={[s.parent, !fCat?.nouveauParentId && s.parentOn]} onPress={() => { setFCat(x => ({ ...x, nouveauParentId: null })); setPick(false); }}>
                    <Text style={s.parentTxt}>{inv?.emoji || '📦'} {t('inventaire.racine')}</Text>
                  </TouchableOpacity>
                  {cats.filter(o => !exclusCat.has(o.id)).map(o => (
                    <TouchableOpacity key={o.id} style={[s.parent, fCat?.nouveauParentId === o.id && s.parentOn]} onPress={() => { setFCat(x => ({ ...x, nouveauParentId: o.id })); setPick(false); }}>
                      <Text style={s.parentTxt}>{'   '.repeat(o.profondeur)}{o.emoji || '📁'} {o.nom}</Text>
                    </TouchableOpacity>
                  ))}
                </>
              ) : (
                <>
                  <Text style={s.lbl}>{t('listes.nom')}</Text>
                  <TextInput style={s.input} value={fCat?.nom || ''} autoFocus={!fCat?.id}
                    onChangeText={(v) => setFCat(x => ({ ...x, nom: v }))}
                    placeholder={t('inventaire.phNomCategorieEx')} placeholderTextColor={UI.faint} />
                  <Text style={s.lbl}>{t('inventaire.rangerDansLabel')}</Text>
                  <TouchableOpacity style={s.parent} onPress={() => setPick(true)}>
                    <Text style={s.parentTxt}>{fCat?.nouveauParentId ? cheminDe(arbre, fCat.nouveauParentId) : `${inv?.emoji || '📦'} ${t('inventaire.racine')}`}</Text>
                    <Text style={s.chevMini}>›</Text>
                  </TouchableOpacity>
                  <Text style={s.lbl}>{t('profil.icone')}</Text>
                  <SelecteurEmoji inline avecRecherche valeur={fCat?.emoji || '📁'} onChange={(e) => setFCat(x => ({ ...x, emoji: e }))} />
                </>
              )}
      </Feuille>
    </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page:  { flex: 1, backgroundColor: UI.bg },
  corps: { padding: 14, paddingTop: 8 },
  hIc:   { fontSize: 18, color: UI.accent },
  hIcOn: { opacity: 1 },
  err:    { backgroundColor: UI.dangerTint, borderWidth: 1, borderColor: UI.danger, borderRadius: 10, padding: 11, margin: 12, marginBottom: 0 },
  errTxt: { color: UI.danger, fontSize: 12.5, fontWeight: '700' },
  info:    { backgroundColor: UI.accentTint, borderWidth: 1, borderColor: UI.accent, borderRadius: 10, padding: 11, margin: 12, marginBottom: 0 },
  infoTxt: { color: UI.accent, fontSize: 12.5, fontWeight: '600' },
  barre:    { paddingHorizontal: 16, paddingTop: 10 },
  barreTxt: { fontSize: 12, color: UI.muted, fontWeight: '600' },
  rechRow:   { flexDirection: 'row', alignItems: 'center', gap: 8, marginHorizontal: 14, marginTop: 8,
               backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 11, paddingVertical: 6,
               borderWidth: 0.5, borderColor: UI.champBord },
  rechIc:    { fontSize: 14, color: UI.faint },
  rechInput: { flex: 1, fontSize: 14, color: UI.ink, paddingVertical: 2 },
  toggle:    { width: 32, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  toggleOn:  { backgroundColor: UI.accentTint },
  toggleIc:  { fontSize: 15 },
  hint:   { fontSize: 11, color: UI.faint, marginHorizontal: 16, marginTop: 6 },
  cat:    { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 10, paddingHorizontal: 8,
            borderRadius: 10, backgroundColor: UI.card, borderWidth: 1, borderColor: UI.border, marginBottom: 6 },
  catChev:  { fontSize: 12, color: UI.faint, width: 12 },
  catEmoji: { fontSize: 19 },
  catNom:   { flex: 1, fontSize: 15, fontWeight: '800', color: UI.ink },
  catBadge: { fontSize: 12.5, fontWeight: '800', color: UI.muted, minWidth: 26, textAlign: 'center',
              borderWidth: 1, borderColor: UI.border, borderRadius: 999, paddingHorizontal: 7, paddingVertical: 2 },
  catPlus:  { fontSize: 18, color: UI.accent, fontWeight: '600', paddingHorizontal: 2 },
  cfgIntro: { fontSize: 12.5, color: UI.muted, marginBottom: 12 },
  cfgCard:  { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 13,
              borderWidth: 1, borderColor: UI.border, padding: 14, marginBottom: 10 },
  cfgEmoji: { fontSize: 24, width: 30, textAlign: 'center' },
  cfgTitre: { fontSize: 15, fontWeight: '800', color: UI.ink },
  cfgSous:  { fontSize: 12, color: UI.muted, marginTop: 2 },
  cfgChev:  { fontSize: 22, color: UI.faint },
  modeleInfo: { fontSize: 12.5, color: UI.muted, lineHeight: 18, marginBottom: 4 },
  addHere:  { paddingVertical: 9, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1, borderStyle: 'dashed',
              borderColor: UI.accent, backgroundColor: UI.accentTint, marginBottom: 8, alignItems: 'center' },
  addHereTxt: { fontSize: 12.5, fontWeight: '700', color: UI.accent },
  catRow:  { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 12,
             borderWidth: 1, borderColor: UI.border, padding: 12, marginBottom: 8 },
  sansCat: { fontSize: 11.5, fontWeight: '800', color: UI.faint, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 10, marginBottom: 6 },
  elem:       { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 11,
                borderWidth: 1, borderColor: UI.border, padding: 10 },
  elemAlerte: { borderColor: UI.danger, backgroundColor: UI.dangerTint },
  pastilleCompte: { width: 9, height: 9, borderRadius: 999, backgroundColor: UI.border },
  pastilleOk: { backgroundColor: '#22C55E' },
  pastilleKo: { backgroundColor: UI.danger },
  elemPhoto:  { width: 34, height: 34, borderRadius: 7, backgroundColor: UI.champ },
  elemEmoji:  { fontSize: 20, width: 34, textAlign: 'center' },
  elemNom:    { fontSize: 14.5, fontWeight: '700', color: UI.ink },
  elemSous:   { fontSize: 11.5, color: UI.muted, marginTop: 1 },
  badge:      { fontSize: 16, fontWeight: '800', color: UI.ink, minWidth: 26, textAlign: 'right' },
  chevMini:   { fontSize: 16, color: UI.faint, width: 14, textAlign: 'center' },
  compter:    { borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, backgroundColor: UI.accentTint, borderWidth: 1, borderColor: UI.accent },
  compterTxt: { fontSize: 12, color: UI.accent, fontWeight: '700' },
  art:      { flexDirection: 'row', alignItems: 'center', gap: 9, backgroundColor: UI.bg, borderRadius: 9,
              borderWidth: 0.5, borderColor: UI.border, paddingVertical: 7, paddingHorizontal: 9 },
  artPhoto: { width: 28, height: 28, borderRadius: 6, backgroundColor: UI.champ },
  artEmoji: { fontSize: 15, width: 28, textAlign: 'center' },
  artNom:   { fontSize: 13.5, fontWeight: '600', color: UI.ink },
  artSous:  { fontSize: 11, color: UI.faint, marginTop: 1 },
  artLieu:  { fontSize: 12, color: UI.accent, fontWeight: '600', marginTop: 2 },
  artQte:   { fontSize: 14, fontWeight: '800', color: UI.accent, minWidth: 22, textAlign: 'right' },
  fond:    { flex: 1, backgroundColor: UI.overlay, justifyContent: 'flex-end' },
  feuille: { height: '82%', backgroundColor: UI.bg, borderTopLeftRadius: 18, borderTopRightRadius: 18, overflow: 'hidden' },
  feuilleCorps: { padding: 16 },
  lbl:    { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  input:  { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  parent:   { flexDirection: 'row', alignItems: 'center', backgroundColor: UI.champ, borderRadius: 10, borderWidth: 0.5, borderColor: UI.champBord, paddingHorizontal: 12, paddingVertical: 11, marginBottom: 6 },
  parentOn: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  parentTxt:{ flex: 1, fontSize: 13.5, color: UI.ink },
});
